import {CheckIcon, CopyIcon} from '@primer/octicons-react'
import {useEffect, useRef, useState} from 'react'

import {copyText} from './copy'

const copyConfirmationMsDelay = 2000

export interface UseCopyToClipboardOptions<Visual = typeof CopyIcon> {
  /**
   * Optional callback that is invoked after copying is initiated.
   */
  onCopy?: () => void
  /**
   * Text to copy, or a function evaluated synchronously when copying is initiated.
   */
  textToCopy: string | (() => string)
  /**
   * Visual displayed before the copy action succeeds.
   * @default CopyIcon
   */
  visual?: Visual
}

export function useCopyToClipboard<Visual = typeof CopyIcon>({
  onCopy,
  textToCopy,
  visual,
}: UseCopyToClipboardOptions<Visual>) {
  const [copied, setCopied] = useState(false)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current !== undefined) clearTimeout(timeoutIdRef.current)
    }
  }, [])

  const copyToClipboard = () => {
    const text = typeof textToCopy === 'function' ? textToCopy() : textToCopy
    setCopied(true)
    void copyText(text)
    onCopy?.()

    if (timeoutIdRef.current !== undefined) clearTimeout(timeoutIdRef.current)
    timeoutIdRef.current = setTimeout(() => {
      setCopied(false)
      timeoutIdRef.current = undefined
    }, copyConfirmationMsDelay)
  }

  return {
    announcement: copied ? 'Copied!' : undefined,
    copied,
    copyToClipboard,
    visual: copied ? CheckIcon : (visual ?? CopyIcon),
  }
}
